import { useContext, useEffect, useState } from 'react';
import api from '../api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Store } from '../Store';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import { useTranslation } from 'react-i18next';

export default function SigninScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen
  const { search } = useLocation(); // accesses the query string portion of the URL, including the leading ? character and any key-value pairs separated by & characters
  const redirectInUrl = new URLSearchParams(search).get('redirect'); // gets the value to the 'redirect' key from the query string portion of the URL (in this case '/shipping-address')
  const redirect = redirectInUrl ? redirectInUrl : '/'; // if redirectInUrl exists, redirect will be set to the redirectURL, otherwise to '/'

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives access to the state of the context (in this case the Store) and lets me rename dispatch to ctxDisptach, to distinguish it from the dispatch of the current component
  const { userInfo, cart } = state;

  const submitHandler = async (e) => {
    e.preventDefault(); // prevents a page refresh, whenever the signin button is clicked
    setLoading(true);
    try {
      const currentLanguage = i18n.language; // Gets the current language
      const baseUrl = window.location.origin;
      const { data } = await api.post('/api/users/signin', {
        email,
        password,
        baseUrl,
        language: currentLanguage,
      }); // axios post request to '/api/users/signin', that contains the email and password in the body of the post request and stores + extracts the 'data' of the response
      ctxDispatch({ type: 'USER_SIGNIN', payload: data }); // dispatches the 'USER_SIGNIN' action to the Store (updates the state of the application)
      localStorage.setItem('userInfo', JSON.stringify(data)); // safes the user information in the browsers local storage (in this case, the localStorage is being modified here and not in the dispatched action in the store, because the user information stored in localStorage is not directly related to the state management in the Store (seperation of concerns). They both need to store the same data, but the Store manages the application state, which includes the signed-in status of the user, but the user information stored in localStorage (for example the JSON web token) is not necessarily part of the application state managed by the Store. Even though the modification of the data is the same in both cases, only if the data necessary to the localStorage 100% correlates with the data necessary to the application state, they can both be handled in the action of the Store)
      toast.success(t('signin-screen-success'));
      navigate(redirect);
    } catch (err) {
      const errorMessage = getError(err);
      if (errorMessage.includes('email-not-verified')) {
        toast.info(t('signin-screen-email-not-verified'));
      } else if (errorMessage.includes('one-verification-email-per-hour')) {
        toast.warning(t('signin-screen-one-verification-email-per-hour'));
      } else if (errorMessage.includes('maximum-number-of-attempts')) {
        toast.error(t('signin-screen-maximum-number-of-attempts'));
      } else if (errorMessage.includes('unknown-fail')) {
        toast.error(t('signin-screen-unknown-fail'));
      } else if (
        errorMessage.includes('backend-user-routes-invalid-email-or-password')
      ) {
        toast.error(t('signin-screen-invalid-email-or-password'));
      } else {
        toast.error(t('signin-screen-unknown-error'));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (cart.cartItems.length === 0 && userInfo) {
      navigate('/'); // if there are no items in the cart and the user is already signed in, the user cant access this screen
    } else if (cart.cartItems.length > 0 && userInfo && redirect === '/cart') {
      navigate('/'); // if the user is navigating back from PlaceOrder -> PaymentMethod -> ShippingAddress -> Signin, the user will be redirected to the HomeScreen
    } else if (userInfo) {
      navigate(redirect); // when the user is already signed-in, he can't enter the SigninScreen and insted will be redirected to the redirect variable (mostly the Screen he is coming from)
    }
  }, [navigate, cart, redirect, userInfo]); // (the dependency array holds all variable that are being used)

  return (
    <Container>
      <div className="container small-container">
        <title>{t('signin-screen-helmet')}</title>

        <h1 className="screen-heading">{t('signin-screen-heading')}</h1>

        <Form onSubmit={submitHandler} className="signin-form">
          <Form.Group controlId="email">
            <Form.Label>{t('signin-screen-email')}</Form.Label>
            <Form.Control
              className="mobile-device-compatible"
              required
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="password">
            <Form.Label>{t('signin-screen-password')}</Form.Label>
            <Form.Control
              className="mobile-device-compatible"
              type={passwordVisible ? 'text' : 'password'}
              required
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="show-password" controlId="passwordVisibility">
            <Form.Check
              className="view-password"
              type="checkbox"
              label={t('signin-screen-show-password')}
              onChange={() => setPasswordVisible(!passwordVisible)}
              checked={passwordVisible}
            />
          </Form.Group>

          <div className="text-center">
            <Button variant="primary" type="submit" disabled={loading}>
              {loading
                ? t('signin-screen-signing-in')
                : t('signin-screen-signin')}
            </Button>
          </div>

          <div className="signup-link">
            <span className="left-link-dash">{'-'}</span>
            <span className="signup-link-info">
              {t('signin-screen-new-customer')}
            </span>
            <span>
              <Link
                className="text-link"
                style={{ whiteSpace: 'nowrap' }}
                to={`/signup?redirect=${redirect}`}
              >
                {t('signin-screen-create-account')}
              </Link>
            </span>
            <span className="right-link-dash">{'-'}</span>
          </div>

          <div className="reset-password-link">
            <span className="left-link-dash">{'-'}</span>
            <span className="reset-password-link-info">
              {t('signin-screen-forgot-password')}
            </span>
            <span>
              <Link
                className="text-link"
                style={{ whiteSpace: 'nowrap' }}
                to={`/forgot-password`}
              >
                {t('signin-screen-reset-password')}
              </Link>
            </span>
            <span className="right-link-dash">{'-'}</span>
          </div>
        </Form>
      </div>
    </Container>
  );
}
